import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../lib/i18n';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';

const SERVICE_IMAGES = [
  '/images/program-1.jpeg',
  '/images/program-2.jpeg',
  null,
  'https://static.prod-images.emergentagent.com/jobs/cad89257-8cb3-4d77-9374-b5c893e862cb/images/bc4093327e063abd8ff8125ae675ea375b142df7b0f6fa2c6fc68f15c96b1b0d.png',
  'https://images.unsplash.com/photo-1768388142279-943d129010cf?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80',
];

export const Services = () => {
  const { t } = useI18n();
  const [openIdx, setOpenIdx] = useState(null);
  const list = t.services.list;

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-cream text-ink py-24 md:py-32 lg:py-40 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="mb-6 md:mb-10">
          <h2
            data-testid="services-heading"
            className="font-display font-medium text-ink text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
          >
            {t.services.heading}<span className="text-violet">.</span>
          </h2>
        </div>

        <ul className="border-t border-ink/15">
          {list.map((item, idx) => {
            const accents = ['text-violet', 'text-violet-deep', 'text-violet', 'text-violet-deep', 'text-violet'];
            const hasDetails = !!(item.paragraphs || item.long || item.details);
            return (
              <li key={idx} className="border-b border-ink/15">
                <button
                  onClick={() => hasDetails && setOpenIdx(idx)}
                  disabled={!hasDetails}
                  data-testid={`service-row-${idx}`}
                  className={`program-row w-full text-left py-4 md:py-5 grid grid-cols-12 gap-4 md:gap-6 items-baseline group ${hasDetails ? 'hover:text-violet cursor-pointer' : 'cursor-default'}`}
                >
                  <span className={`col-span-2 md:col-span-1 svc-num text-[11px] md:text-sm tracking-[0.2em] font-body ${accents[idx]} opacity-70 group-hover:opacity-100 transition-opacity`}>
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <h3 className="col-span-10 md:col-span-5 font-display font-medium text-xl md:text-2xl lg:text-3xl leading-[1.15] tracking-tight">
                    {item.subtitle || item.name}
                  </h3>
                  <p className="col-span-12 md:col-span-4 font-body text-sm text-ink/60 leading-relaxed group-hover:text-ink/80 transition-colors">
                    {item.short}
                  </p>
                  <div className="col-span-12 md:col-span-2 flex md:justify-between items-baseline gap-3">
                    <span className="text-[10px] tracking-[0.32em] uppercase text-ink/45 font-body group-hover:text-violet transition-colors">
                      {item.duration}
                    </span>
                    {hasDetails && (
                      <span aria-hidden="true" className={`arrow font-display text-xl ${accents[idx]}`}>→</span>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <Dialog open={openIdx !== null} onOpenChange={(o) => !o && setOpenIdx(null)}>
        <DialogContent
          data-testid="service-dialog"
          className="max-w-4xl bg-ink border-cream/10 text-cream p-0 overflow-hidden max-h-[92vh] overflow-y-auto [&>button]:text-cream [&>button]:hover:text-peach"
        >
          {openIdx !== null && (
            <div className={SERVICE_IMAGES[openIdx] ? "grid md:grid-cols-5" : "block"}>
              {SERVICE_IMAGES[openIdx] && (
                <div className="relative h-64 md:h-auto min-h-[280px] md:col-span-2 overflow-hidden">
                  <img src={SERVICE_IMAGES[openIdx]} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent md:bg-gradient-to-r" />
                </div>
              )}
              <div className={`p-8 md:p-10 lg:p-12 flex flex-col ${SERVICE_IMAGES[openIdx] ? 'md:col-span-3' : ''}`}>
                <span className="text-[10px] tracking-[0.4em] uppercase text-peach/80 mb-4 font-body">
                  — {String(openIdx + 1).padStart(2, '0')} · {list[openIdx].duration}
                </span>
                <DialogTitle className="font-display font-medium text-2xl md:text-3xl lg:text-4xl leading-tight text-cream mb-2">
                  {list[openIdx].name}
                </DialogTitle>
                {list[openIdx].subtitle && (
                  <p className="font-body text-peach/85 text-sm md:text-base mb-6">
                    {list[openIdx].subtitle}
                  </p>
                )}

                {list[openIdx].paragraphs ? (
                  <DialogDescription asChild>
                    <div className="space-y-4 text-cream/85 font-body leading-relaxed text-[15px] mb-6">
                      {list[openIdx].paragraphs.map((p, i) => (
                        <p key={i}>{p}</p>
                      ))}
                    </div>
                  </DialogDescription>
                ) : (
                  <DialogDescription className="text-cream/80 font-body leading-relaxed text-base mb-6">
                    {list[openIdx].long}
                  </DialogDescription>
                )}

                {list[openIdx].details && (
                  <dl className="grid grid-cols-[max-content_1fr] gap-x-5 gap-y-2 text-[13px] font-body border-t border-cream/10 pt-5 mb-6">
                    {list[openIdx].details.map((d, i) => (
                      <React.Fragment key={i}>
                        <dt className="text-peach/75 tracking-[0.18em] uppercase text-[10px] pt-1">
                          {d.label}
                        </dt>
                        <dd className="text-cream/90">{d.value}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                )}

                {list[openIdx].host && (() => {
                  const linkTo = list[openIdx].host.linkTo || '/about';
                  const isAnchor = linkTo.startsWith('#');
                  const klass = "group inline-flex items-center gap-3 self-start border border-peach/60 text-peach px-4 py-2 hover:bg-peach hover:text-ink transition-colors duration-500 text-[10px] tracking-[0.32em] uppercase font-body mb-6";
                  const label = (
                    <>
                      <span>{list[openIdx].host.label}</span>
                      <span aria-hidden="true" className="font-display group-hover:translate-x-1 transition-transform">→</span>
                    </>
                  );
                  if (isAnchor) {
                    return (
                      <a
                        href={linkTo}
                        onClick={(e) => {
                          e.preventDefault();
                          setOpenIdx(null);
                          setTimeout(() => {
                            document.querySelector(linkTo)?.scrollIntoView({ behavior: 'smooth' });
                          }, 200);
                        }}
                        className={klass}
                        data-testid="service-dialog-host"
                      >
                        {label}
                      </a>
                    );
                  }
                  return (
                    <Link
                      to={linkTo}
                      onClick={() => setOpenIdx(null)}
                      className={klass}
                      data-testid="service-dialog-host"
                    >
                      {label}
                    </Link>
                  );
                })()}

                <div className="mt-auto flex items-center justify-between gap-4 pt-4 border-t border-cream/10">
                  <button
                    onClick={() => setOpenIdx(null)}
                    data-testid="service-dialog-close"
                    className="text-[10px] tracking-[0.4em] uppercase text-cream/70 hover:text-peach transition-colors font-body"
                  >
                    {t.services.close}
                  </button>
                  <a
                    href="#connect"
                    onClick={(e) => {
                      e.preventDefault();
                      setOpenIdx(null);
                      setTimeout(() => {
                        document.getElementById('connect')?.scrollIntoView({ behavior: 'smooth' });
                      }, 200);
                    }}
                    className="border-b border-peach pb-1 text-peach hover:text-cream hover:border-cream text-[10px] tracking-[0.4em] uppercase font-body"
                    data-testid="service-dialog-enquire"
                  >
                    {t.connect.title}
                  </a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Services;
